import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Input } from '../components/ui/input';
import {
  Trophy, TrendingUp, FileText, Scissors, CalendarCheck, Download, PhoneCall, Megaphone,
} from 'lucide-react';
import { formatEUR, formatDate } from '../lib/api';
import { Button } from '../components/ui/button';
import MarcaLead, { nombreMarca } from '../components/MarcaLead';
import RecuentoProcedencia from '../components/RecuentoProcedencia';

/**
 * Indicadores — objetivos comerciales por trabajador (solo administradores).
 * Quién entrega y envía presupuestos y, sobre todo, quién agenda cirugías:
 * al final de mes se recompensa al que más haya conseguido.
 */
export default function IndicadoresPage() {
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [datos, setDatos] = useState(null);
  const [falloDatos, setFalloDatos] = useState('');
  // Leads contactados y por quién. Va aparte del resto: el marcador de leads
  // no es de un mes, es de toda la lista.
  const [leads, setLeads] = useState(null);
  const [falloLeads, setFalloLeads] = useState('');

  useEffect(() => {
    let off = false;
    setDatos(null);
    setFalloDatos('');
    api.get('/api/indicadores/comercial', { params: { mes } })
      .then((r) => { if (!off) { setDatos(r.data); setFalloDatos(''); } })
      // Un fallo de lectura NO es un mes sin ventas: dirección leía «sin
      // actividad» justo el mes de la recompensa (medido el 22/9).
      .catch((e) => {
        if (!off) { setDatos({ filas: [] }); setFalloDatos(e?.response?.data?.detail || 'no se ha podido consultar'); }
      });
    return () => { off = true; };
  }, [mes]);

  useEffect(() => {
    let off = false;
    api.get('/api/leads/marcador')
      .then((r) => { if (!off) { setLeads(r.data); setFalloLeads(''); } })
      // Un cero porque la consulta falló se leería como «no ha llamado nadie»:
      // se dice que no se ha podido leer.
      .catch((e) => { if (!off) setFalloLeads(e?.response?.data?.detail || 'no se ha podido leer'); });
    return () => { off = true; };
  }, []);

  const filas = datos?.filas || [];
  const detalle = datos?.detalle || [];
  const totales = datos?.totales || {};
  const [filtro, setFiltro] = useState('todo');   // todo | cirugia | presupuesto | primera_valoracion
  const lineas = filtro === 'todo' ? detalle : detalle.filter((d) => d.tipo === filtro);

  const ETIQUETA = {
    cirugia: 'Cirugía',
    presupuesto: 'Presupuesto',
    primera_valoracion: '1ª valoración',
  };

  const descargar = () => {
    const cab = ['Fecha', 'Tipo', 'Paciente', 'NIF', 'Detalle', 'Agendado por'];
    const filasCsv = lineas.map((d) => [
      d.fecha, ETIQUETA[d.tipo] || d.tipo, d.paciente, d.nif || '',
      d.referencia || '', d.trabajador,
    ]);
    const csv = [cab, ...filasCsv]
      .map((f) => f.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `indicadores_${mes}.csv`;
    a.click();
  };
  const maxCirugias = filas.length ? Math.max(...filas.map((f) => f.cirugias_agendadas)) : 0;
  // Cuántos contactados entran en el reparto por persona (con el bot).
  const repartidos = (leads?.marcador || []).reduce((s, p) => s + (p.leads || 0), 0)
    + (leads?.por_el_bot || 0);
  // Las marcas que ha dado de alta la clínica (las manda el servidor con el
  // marcador): una columna por marca y otra para lo que no se sabe de cuál
  // es. Sin marcas (o con un servidor viejo) no hay columnas, en vez de
  // enseñar ceros.
  const marcas = Array.isArray(leads?.marcas) ? leads.marcas : [];
  const columnasMarca = marcas.length ? [...marcas.map((m) => m.clave), 'sin'] : [];
  // Contactados y pendientes de cada marca, sobre toda la lista.
  const porMarca = columnasMarca.length ? (leads?.por_marca || null) : null;
  // Los leads por procedencia: las campañas de cada tratamiento y las demás
  // vías. Viene con el marcador; un servidor viejo no lo manda.
  const porProcedencia = leads?.por_procedencia?.filas ? leads.por_procedencia : null;

  return (
    <div>
      <PageHeader
        title="Indicadores"
        subtitle="Objetivos comerciales del mes, por trabajador. La cuenta de cirugías es por fecha de agendado."
      />

      <div className="cp-card p-3 mb-4 flex items-center gap-3">
        <span className="text-sm font-medium text-[#13231E]">Mes:</span>
        <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-44 h-9" data-testid="indicadores-mes" />
      </div>

      {/* Totalización del mes (2026-08-14): lo primero que se mira. */}
      {datos && filas.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="indicadores-totales">
          {[
            { k: 'primeras_valoraciones', label: '1ª valoraciones citadas', icono: CalendarCheck },
            { k: 'presupuestos_entregados', label: 'Presupuestos entregados', icono: FileText },
            { k: 'cirugias_agendadas', label: 'Cirugías agendadas', icono: Scissors, fuerte: true },
          ].map(({ k, label, icono: Ico, fuerte }) => (
            <div key={k} className={`cp-card p-4 ${fuerte ? 'border-2 border-[#2E6E5E]/40' : ''}`}>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Ico className="w-3.5 h-3.5 text-[#2E6E5E]" /> {label}
              </div>
              <div className={`mt-1 tabular-nums font-bold ${fuerte ? 'text-3xl text-[#2E6E5E]' : 'text-2xl text-[#13231E]'}`}>
                {totales[k] ?? 0}
              </div>
            </div>
          ))}
          <div className="cp-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <FileText className="w-3.5 h-3.5 text-[#2E6E5E]" /> Importe presupuestado
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-[#13231E]">
              {formatEUR(totales.importe_presupuestado || 0)}
            </div>
          </div>
        </div>
      )}

      <div className="cp-card overflow-hidden">
        {datos === null ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : filas.length === 0 ? (
          falloDatos ? (
            <p className="text-sm text-red-700" data-testid="indicadores-fallo">
              No se ha podido consultar este mes ({falloDatos}). Esto no es un cero:
              vuelve a intentarlo.
            </p>
          ) : (
            <EmptyState icon={TrendingUp} title="Sin actividad" description="No hay movimientos comerciales en ese mes." />
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Trabajador</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Primeras valoraciones citadas</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Presupuestos entregados</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Cirugías agendadas 🏆</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const lider = maxCirugias > 0 && f.cirugias_agendadas === maxCirugias;
                  return (
                    <tr key={f.trabajador} className={`border-t border-border ${lider ? 'bg-amber-50/60 dark:bg-amber-950/20' : 'hover:bg-[rgba(46,110,94,0.03)]'}`} data-testid="indicadores-fila">
                      <td className="py-2.5 px-4 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {lider && <Trophy className="w-4 h-4 text-amber-500" />}
                          {f.trabajador}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-right tabular-nums">{f.primeras_valoraciones}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums">{f.presupuestos_entregados}</td>
                      <td className={`py-2.5 px-4 text-right tabular-nums font-bold ${lider ? 'text-amber-600' : ''}`}>
                        {f.cirugias_agendadas}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* DETALLE línea a línea: quién agendó qué y para qué paciente. */}
      {detalle.length > 0 && (
        <div className="cp-card overflow-hidden mt-4" data-testid="indicadores-detalle">
          <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border">
            <span className="text-sm font-semibold text-[#13231E]">Detalle del mes</span>
            <div className="flex gap-1">
              {[['todo', 'Todo'], ['cirugia', 'Cirugías'], ['presupuesto', 'Presupuestos'],
                ['primera_valoracion', '1ª valoraciones']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setFiltro(v)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium border ${
                    filtro === v ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                                 : 'bg-background text-muted-foreground border-border hover:text-foreground'}`}>
                  {l}
                </button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{lineas.length} líneas</span>
            <Button size="sm" variant="outline" className="ml-auto h-8" onClick={descargar}
              data-testid="indicadores-descargar">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left sticky top-0">
                <tr>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Fecha</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Tipo</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Paciente</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Detalle</th>
                  <th className="py-2 px-3 font-semibold text-[#13231E]">Agendado por</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((d, i) => (
                  <tr key={`${d.tipo}-${d.patient_id}-${i}`} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]"
                    data-testid="indicadores-linea">
                    <td className="py-2 px-3 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                      {formatDate(d.fecha)}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        d.tipo === 'cirugia' ? 'bg-[#2E6E5E] text-white'
                          : d.tipo === 'presupuesto' ? 'bg-[rgba(46,110,94,0.14)] text-[#13231E]'
                          : 'bg-muted text-muted-foreground'}`}>
                        {ETIQUETA[d.tipo] || d.tipo}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-medium">
                      {d.paciente || '—'}
                      {d.nif && <span className="text-xs text-muted-foreground"> · {d.nif}</span>}
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {d.referencia}
                      {d.importe ? ` · ${formatEUR(d.importe)}` : ''}
                      {d.fecha_cirugia ? ` · opera el ${formatDate(d.fecha_cirugia)}` : ''}
                      {d.fecha_cita ? ` · cita el ${formatDate(d.fecha_cita)}` : ''}
                    </td>
                    <td className="py-2 px-3 font-medium whitespace-nowrap">{d.trabajador}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Leads contactados */}
      <div className="mt-8" data-testid="indicadores-leads">
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <PhoneCall className="w-4 h-4" /> Leads contactados
        </h3>
        {falloLeads ? (
          <p className="text-sm text-red-700" data-testid="indicadores-leads-fallo">
            No se ha podido leer el marcador de leads ({falloLeads}). Esto no es un cero:
            es que no se ha podido consultar.
          </p>
        ) : !leads ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : (leads.marcador || []).length === 0 && !leads.por_el_bot ? (
          <p className="text-sm text-muted-foreground">
            Todavía no se ha contactado con ningún lead
            {leads.pendientes ? ` (${leads.pendientes} esperando)` : ''}.
            {porMarca && leads.pendientes ? (
              <span className="block text-xs mt-1" data-testid="indicadores-leads-esperando-marca">
                {columnasMarca.map((m) => `${nombreMarca(marcas, m)}: ${porMarca[m]?.pendientes ?? 0}`).join(' · ')}
              </span>
            ) : null}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${porMarca ? 'max-w-3xl' : 'max-w-md'}`}>
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-3">Quién</th>
                  <th className="py-2 pr-3 text-right">Contactados</th>
                  {/* Por marca: cuántos de cada una. */}
                  {porMarca ? columnasMarca.map((m) => (
                    <th key={m} className="py-2 pr-3 text-right" data-testid={`indicadores-leads-col-${m}`}>
                      <MarcaLead marca={m === 'sin' ? null : m} marcas={marcas} />
                    </th>
                  )) : null}
                </tr>
              </thead>
              <tbody>
                {/* La clave es la PERSONA (M15, 23/9/2026): con el nombre, dos
                    personas con el mismo nombre eran la misma fila para React. Quien ya no
                    está en la casa llega sin id y se queda con su nombre,
                    que es como el servidor la agrupa. */}
                {leads.marcador.map((p) => (
                  <tr key={p.id || `nombre:${p.nombre}`} className="border-b last:border-0"
                    data-testid={`indicadores-leads-${p.id || p.nombre}`}>
                    <td className="py-2 pr-3">{p.nombre}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{p.leads}</td>
                    {porMarca ? columnasMarca.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums"
                        data-testid={`indicadores-leads-${p.id || p.nombre}-${m}`}>
                        {leads.por_marca_por_persona?.[p.id || `nombre:${p.nombre}`]?.[m] ?? 0}
                      </td>
                    )) : null}
                  </tr>
                ))}
                {leads.por_el_bot ? (
                  <tr className="text-xs text-muted-foreground" data-testid="indicadores-leads-bot">
                    <td className="py-2 pr-3">Marcados por el conector (no cuentan para nadie)</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{leads.por_el_bot}</td>
                    {porMarca ? columnasMarca.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums">
                        {leads.por_el_bot_por_marca?.[m] ?? 0}
                      </td>
                    )) : null}
                  </tr>
                ) : null}
                <tr className="text-xs text-muted-foreground">
                  <td className="py-2 pr-3">Total · {leads.pendientes} sin contactar</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{leads.contactados}</td>
                  {porMarca ? columnasMarca.map((m) => (
                    <td key={m} className="py-2 pr-3 text-right tabular-nums"
                      data-testid={`indicadores-leads-total-${m}`}>
                      {porMarca[m]?.contactados ?? 0}
                    </td>
                  )) : null}
                </tr>
                {porMarca ? (
                  <tr className="text-xs text-muted-foreground" data-testid="indicadores-leads-pendientes-marca">
                    <td className="py-2 pr-3">Sin contactar</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{leads.pendientes}</td>
                    {columnasMarca.map((m) => (
                      <td key={m} className="py-2 pr-3 text-right tabular-nums"
                        data-testid={`indicadores-leads-pendientes-${m}`}>
                        {porMarca[m]?.pendientes ?? 0}
                      </td>
                    ))}
                  </tr>
                ) : null}
              </tbody>
            </table>
            {/* Si el reparto no cabe entero se DICE, como en la lista de
                Leads (M15): un marcador cortado en silencio se lee como el
                marcador entero. El total de abajo sí es el de toda la lista. */}
            {/* De los contactados, cuántos ya tienen cita (27/09/2026): esos
                ya no están en la lista de Leads, pero el punto es de quien
                llamó. */}
            {leads.ya_citados ? (
              <p className="text-xs text-muted-foreground mt-2" data-testid="indicadores-leads-ya-citados">
                De los {leads.contactados} contactados, {leads.ya_citados} ya tienen cita.
              </p>
            ) : null}
            {leads.truncado ? (
              <p className="text-xs text-amber-700 mt-2" data-testid="indicadores-leads-truncado">
                El reparto por persona cuenta los {repartidos} contactados más recientes
                de {leads.contactados}; el total sí es el de toda la lista.
              </p>
            ) : null}
          </div>
        )}
      </div>

      {/* Leads por procedencia: «para tener un buen recuento de qué trae cada
          cosa». */}
      {porProcedencia ? (
        <div className="mt-8" data-testid="indicadores-procedencia">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
            <Megaphone className="w-4 h-4" /> Leads por procedencia
          </h3>
          {porProcedencia.filas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay leads.</p>
          ) : (
            <RecuentoProcedencia filas={porProcedencia.filas} totales={porProcedencia.totales}
              testid="indicadores-procedencia-tabla" />
          )}
          {porProcedencia.completo === false ? (
            <p className="text-xs text-amber-700 mt-2" data-testid="indicadores-procedencia-cortado">
              Hay más leads de los que se pueden contar de una vez: estos números no son de toda la lista.
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground mt-3">
        «Primeras valoraciones» son las consultas previas (presenciales y online) que cada
        persona ha CITADO ese mes, no las que se atienden: cuenta cuándo se consigue la cita.
        No entran las primeras revisiones, que son curas de después de operar.
        «Entregados» cuenta los presupuestos creados por cada persona ese mes. Lo anulado no cuenta.
        <br />
        El detalle no se teclea: el paciente y quien lo agendó salen de lo que guarda el
        programa al crear cada cita o presupuesto, así que no se puede apuntar algo que no
        existe ni ponerlo a nombre de otra persona.
        <br />
        «Leads contactados» cuenta los contactos de Leads/Contactos que alguien ha marcado
        como atendidos, y sale de quién pulsó el botón: tampoco se teclea. Si la clínica ha
        dado de alta marcas (Configuración), la de cada uno sale del primer correo de una
        marca al que escribió o de «de dónde viene»; lo que no se sabe va en «Sin
        identificar» y no se reparte. Los contactos descartados no cuentan, y quien ya
        tiene cita no cuenta como «sin contactar».
        <br />
        «Leads por procedencia» dice de dónde viene cada contacto: la campaña de su
        tratamiento (lo que diga «de dónde viene» o lo puesto a mano en Leads) o una de
        las demás vías. «Recibidos» son todos menos los descartados,
        que van aparte; los ya citados sí cuentan. Pulsa una para ver sus contactos.
      </p>
    </div>
  );
}
